import { UISimulation } from '@/components/help/UISimulation';

const D = 1900;

function Shell({
  step,
  total,
  title,
  children,
}: {
  step: number;
  total: number;
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="rounded-md border border-dash-border bg-dash-bg p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-wide text-dash-muted">
          Step {step} of {total}
        </div>
        <div className="text-[11px] font-mono text-dash-secondary">{title}</div>
      </div>
      {children}
    </div>
  );
}

// Preview modal — 4 frames.
function PVOpen(): JSX.Element {
  return (
    <Shell step={1} total={4} title="Click Preview">
      <div className="rounded border border-dash-border bg-dash-surface p-3 text-xs">
        <div className="flex items-center justify-between">
          <div className="text-dash-secondary">PublishActionBar</div>
          <span className="rounded bg-accent/15 text-accent border border-accent/40 px-2 py-0.5">
            Preview
          </span>
        </div>
        <div className="mt-1 text-[10px] font-mono uppercase text-dash-muted">
          Cmd+Shift+P also opens it
        </div>
      </div>
    </Shell>
  );
}

function PVDevice(): JSX.Element {
  return (
    <Shell step={2} total={4} title="Device toggle">
      <div className="flex gap-1 text-[11px] font-mono">
        {['1280', '820', '390'].map((w, i) => (
          <span
            key={w}
            className={
              i === 1
                ? 'rounded border border-accent/40 bg-accent/15 text-accent px-2 py-0.5'
                : 'rounded border border-dash-border text-dash-muted px-2 py-0.5'
            }
          >
            {w}
          </span>
        ))}
      </div>
      <div className="mt-3 mx-auto w-2/3 aspect-[4/3] rounded border border-dash-border bg-dash-surface p-2">
        <div className="h-2 w-1/3 rounded bg-dash-border" />
        <div className="mt-2 h-1.5 w-2/3 rounded bg-dash-border" />
        <div className="mt-1 h-1.5 w-1/2 rounded bg-dash-border" />
      </div>
    </Shell>
  );
}

function PVZoom(): JSX.Element {
  return (
    <Shell step={3} total={4} title="Zoom">
      <div className="flex gap-1 text-[11px] font-mono">
        {['75%', '100%', '125%'].map((z, i) => (
          <span
            key={z}
            className={
              i === 2
                ? 'rounded border border-accent/40 bg-accent/15 text-accent px-2 py-0.5'
                : 'rounded border border-dash-border text-dash-muted px-2 py-0.5'
            }
          >
            {z}
          </span>
        ))}
      </div>
    </Shell>
  );
}

function PVToc(): JSX.Element {
  return (
    <Shell step={4} total={4} title="Mini-TOC">
      <ul className="space-y-1 text-[11px] font-mono">
        {['Intro', 'Why descaling matters', 'How often', 'What to use', 'Safety'].map(
          (s, i) => (
            <li
              key={s}
              className={
                i === 2
                  ? 'rounded bg-accent/15 text-accent px-2 py-0.5'
                  : 'rounded text-dash-secondary px-2 py-0.5'
              }
            >
              {s}
            </li>
          ),
        )}
      </ul>
    </Shell>
  );
}

export function PreviewModalSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <PVOpen />, label: 'Open preview', duration: D },
        { frame: <PVDevice />, label: 'Switch device width', duration: D },
        { frame: <PVZoom />, label: 'Zoom 125%', duration: D },
        { frame: <PVToc />, label: 'Jump via mini-TOC', duration: D },
      ]}
    />
  );
}

// Unpublish — 3 frames.
function UPRow(): JSX.Element {
  return (
    <Shell step={1} total={3} title="Articles row">
      <div className="rounded border border-dash-border bg-dash-surface p-2 text-xs">
        <div className="flex items-center gap-2">
          <span className="text-dash-primary">When to take protein</span>
          <span className="text-success font-mono text-[10px]">Live</span>
          <span className="ml-auto rounded bg-warning/15 text-warning border border-warning/40 px-2 py-0.5 text-[11px]">
            Unpublish
          </span>
        </div>
      </div>
    </Shell>
  );
}

function UPConfirm(): JSX.Element {
  return (
    <Shell step={2} total={3} title="Confirm">
      <div className="rounded border border-dash-border bg-dash-surface p-3 text-xs">
        <div className="text-dash-primary">
          Unpublish "When to take protein"?
        </div>
        <div className="mt-1 text-dash-secondary">
          The MDX file will be removed from the site repo. Reversible.
        </div>
        <div className="mt-2 flex gap-2">
          <span className="rounded border border-dash-border text-dash-secondary px-2 py-0.5">
            Cancel
          </span>
          <span className="rounded bg-warning/15 text-warning border border-warning/40 px-2 py-0.5">
            Unpublish
          </span>
        </div>
      </div>
    </Shell>
  );
}

function UPGone(): JSX.Element {
  return (
    <Shell step={3} total={3} title="Unpublished">
      <div className="rounded border border-dash-border bg-dash-surface p-2 text-xs">
        <div className="flex items-center gap-2">
          <span className="text-dash-primary">When to take protein</span>
          <span className="text-dash-muted font-mono text-[10px]">Unpublished</span>
        </div>
      </div>
    </Shell>
  );
}

export function UnpublishSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <UPRow />, label: 'Articles row with Unpublish', duration: D },
        { frame: <UPConfirm />, label: 'Confirm the action', duration: D },
        { frame: <UPGone />, label: 'Row drops to Unpublished', duration: D },
      ]}
    />
  );
}

// Bulk operations — 3 frames.
function BOSelect(): JSX.Element {
  return (
    <Shell step={1} total={3} title="Select rows">
      <ul className="space-y-1 text-xs">
        {[true, true, false].map((on, i) => (
          <li
            key={i}
            className="flex items-center gap-2 rounded border border-dash-border bg-dash-surface px-2 py-1"
          >
            <span
              className={
                on
                  ? 'inline-block w-3 h-3 rounded-sm bg-accent border border-accent'
                  : 'inline-block w-3 h-3 rounded-sm border border-dash-border'
              }
            />
            <span className="text-dash-primary">Draft {i + 1}</span>
          </li>
        ))}
      </ul>
    </Shell>
  );
}

function BOBar(): JSX.Element {
  return (
    <Shell step={2} total={3} title="Floating bar">
      <div className="rounded-full border border-accent/40 bg-dash-surface px-3 py-1.5 text-xs flex items-center gap-3">
        <span className="text-dash-primary font-mono">2 selected</span>
        <span className="rounded bg-accent/15 text-accent border border-accent/40 px-2 py-0.5">
          Archive
        </span>
        <span className="rounded border border-dash-border text-dash-secondary px-2 py-0.5">
          Clear
        </span>
      </div>
    </Shell>
  );
}

function BODone(): JSX.Element {
  return (
    <Shell step={3} total={3} title="Archived">
      <div className="text-[10px] font-mono uppercase text-success">
        2 drafts archived
      </div>
      <p className="mt-2 text-xs text-dash-secondary">
        Find them under the Archived section, restore-only for 30 days.
      </p>
    </Shell>
  );
}

export function BulkOperationsSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <BOSelect />, label: 'Tick row checkboxes', duration: D },
        { frame: <BOBar />, label: 'Bulk action bar appears', duration: D },
        { frame: <BODone />, label: 'Action runs', duration: D },
      ]}
    />
  );
}

// Archive / hard-delete — 3 frames.
function ARTrash(): JSX.Element {
  return (
    <Shell step={1} total={3} title="Trash icon">
      <div className="rounded border border-dash-border bg-dash-surface p-2 text-xs flex items-center gap-2">
        <span className="text-dash-primary">Old article</span>
        <span className="ml-auto rounded bg-error/15 text-error border border-error/40 px-2 py-0.5 text-[11px]">
          Archive
        </span>
      </div>
    </Shell>
  );
}

function ARConfirm(): JSX.Element {
  return (
    <Shell step={2} total={3} title="Confirm">
      <div className="rounded border border-dash-border bg-dash-surface p-3 text-xs">
        <div className="text-dash-primary">Archive "Old article"?</div>
        <div className="mt-1 text-dash-secondary">
          It will be hidden from active views. Restorable for 30 days.
        </div>
      </div>
    </Shell>
  );
}

function ARDelete(): JSX.Element {
  return (
    <Shell step={3} total={3} title="Hard delete (admin)">
      <div className="rounded border border-error/40 bg-error/10 p-3 text-xs">
        <div className="text-error font-mono text-[10px] uppercase">
          Admin only · after 30 days
        </div>
        <div className="mt-1 text-dash-primary">
          Type the slug to permanently delete.
        </div>
        <div className="mt-1 rounded border border-dash-border bg-dash-bg px-2 py-1 text-dash-secondary font-mono">
          old-article-slug
        </div>
      </div>
    </Shell>
  );
}

export function ArchiveDeleteSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <ARTrash />, label: 'Click the trash icon', duration: D },
        { frame: <ARConfirm />, label: 'Archive (reversible)', duration: D },
        { frame: <ARDelete />, label: 'Hard delete after 30 days', duration: D },
      ]}
    />
  );
}
